import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useAtomValue, useSetAtom } from 'jotai';
import { Trans, useTranslation } from 'next-i18next';
import Gap from 'src/components/Gap';
import ModalLayoutBase, { MobileModalType, ModalContent, ModalFooter, ModalHeader, ModalHeaderTitle } from 'src/components/ModalLayout';
import { ModalHeaderDescriptionRow, ModalProps } from 'src/components/VividiOnly/Match/Preference/Modal';
import PreferenceSaveButtons from 'src/components/VividiOnly/Match/Preference/PreferenceSaveButtons';
import useGemAmount from 'src/hooks/useGemAmount';
import useMount from 'src/hooks/useMount';
import useUserData from 'src/hooks/useUserData';
import { userInventoryAtom } from 'src/stores/auth/atoms';
import { isVividiFilterEverOpenedAtom } from 'src/stores/match/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';
import GenderIconBase from './Icon';
import GenderOptionItem from './OptionItem';
import RegionFilter from './RegionFilter';
import useGenderPreference from './useGenderPreference';
const ModalLayout = styled(ModalLayoutBase)`
  width: 400px;
`;
const Row = styled.div`
  display: grid;
  height: min-content;
  grid-gap: 8px;
  grid-template-columns: repeat(3, 1fr);
  background-color: ${({
  theme
}) => theme.color.surface2};
  padding-top: 12px;
  padding-bottom: 16px;
`;
const GenderFilterCouponIcon = styled(GenderIconBase)`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
`;
const Subtitle = styled.div`
  color: ${({
  theme
}) => theme.color.content10};
  padding-top: 12px;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
`;
const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({
  theme
}) => theme.color.content4};
`;
const GenderPreferenceModal: React.FC<ModalProps> = ({
  onClose,
  landFrom
}) => {
  const setIsVividiFilterEverOpened = useSetAtom(isVividiFilterEverOpenedAtom);
  const {
    t
  } = useTranslation();
  const gemAmount = useGemAmount();
  const inventoryItems = useAtomValue(userInventoryAtom);
  const {
    userProfile
  } = useUserData() || {};
  const {
    genderMatchFilter,
    genderMatchFilterOption,
    tryApplyGenderFilterOption,
    genderFilterItemQuantity
  } = useGenderPreference();
  const nextGenderMatchFilterOption = genderMatchFilterOption;
  useMount(() => {
    setIsVividiFilterEverOpened(true);
  });
  const handleClose = () => {
    pushEvent({
      eventType: EVENT_TYPE.VIDEO_CHAT,
      eventName: EVENT_NAME[`${landFrom}__GENDERFILTER_CLOSE`]
    });
    onClose?.();
  };
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.VIDEO_CHAT,
      eventName: EVENT_NAME[`${landFrom}__OPEN_GENDERFILTER`]
    });
  }, [landFrom]);
  if (!genderMatchFilter || gemAmount === undefined || !inventoryItems || !userProfile) {
    return null;
  }
  return <ModalLayout mobileType={MobileModalType.BOTTOM_SHEET} isDim onClose={handleClose} data-testid='Match-GenderPreferenceModal' data-sentry-element="ModalLayout" data-sentry-component="GenderPreferenceModal" data-sentry-source-file="Modal.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="Modal.tsx">
        <ModalHeaderTitle mobileType={MobileModalType.BOTTOM_SHEET} data-sentry-element="ModalHeaderTitle" data-sentry-source-file="Modal.tsx">
          {t('vividi_genderfilter_sheet_title')}
        </ModalHeaderTitle>
        <ModalHeaderDescriptionRow data-sentry-element="ModalHeaderDescriptionRow" data-sentry-source-file="Modal.tsx">
          {!nextGenderMatchFilterOption?.gemCost ? t('vividi_genderfilter_sheet_desc') : <>
              {!!genderFilterItemQuantity && <>
                  <GenderFilterCouponIcon />
                  <Gap width={8} />
                </>}
              <Trans i18nKey={genderFilterItemQuantity ? 'FILTER_GENDER_GUIDE_ITEM' : 'FILTER_GENDER_GUIDE_GEM'} values={{
            itemCount: genderFilterItemQuantity || nextGenderMatchFilterOption?.gemCost
          }} />
            </>}
        </ModalHeaderDescriptionRow>
      </ModalHeader>
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="Modal.tsx">
        <Subtitle data-sentry-element="Subtitle" data-sentry-source-file="Modal.tsx">{t('vividi_genderfilter_sheet_subtitle_gender')}</Subtitle>
        <Row data-sentry-element="Row" data-sentry-source-file="Modal.tsx">
          {genderMatchFilter.filterOptions.map(filterOption => {
          const isActive = filterOption.key === nextGenderMatchFilterOption?.key;
          const handleClickOption = () => {
            if (isActive) return;
            tryApplyGenderFilterOption({
              genderFilterOption: filterOption
            });
          };
          return <GenderOptionItem key={filterOption.key} filterOption={filterOption} isActive={isActive} onClick={handleClickOption} />;
        })}
        </Row>
        <Divider data-sentry-element="Divider" data-sentry-source-file="Modal.tsx" />
        <RegionFilter data-sentry-element="RegionFilter" data-sentry-source-file="Modal.tsx" />
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="Modal.tsx">
        <PreferenceSaveButtons data-testid='Match-GenderPreferenceModal-save-buttons' data-sentry-element="PreferenceSaveButtons" data-sentry-source-file="Modal.tsx" />
      </ModalFooter>
    </ModalLayout>;
};
export default GenderPreferenceModal;